import { NOT_APPLICABLE, TOKEN_NOT_ELIGIBLE_FOR_REFRESH } from '../utils/constants';
import { buildQueryString, type QueryValue } from '../utils/queryString';
import { resolveAccessToken } from './tokenProvider';
import type { ApiRecord, AuthConfig, DelegatedUsersResponse, FeedbackBody } from '../types';

/**
 * Same-origin API client.
 *
 * Every call targets a RELATIVE `/api/...` path. There is no backend host here, no
 * service path fragment, and above all no `Ocp-Apim-Subscription-Key` header — that key
 * now exists only in the Node process's environment. Adding any of them back would undo
 * the entire migration, so treat an absolute URL or a subscription header appearing in
 * this file as a defect, not a shortcut. `scripts/verify-bundle.sh` greps the built
 * bundle for exactly that.
 *
 * The Authorization bearer token still comes from localStorage, exactly as before: the
 * trust model (APIM validates the token) is unchanged by this migration.
 */

export type ApiErrorKind = 'response' | 'network' | 'token-expired';

export class ApiError extends Error {
  readonly status: number | undefined;
  readonly kind: ApiErrorKind;
  /** Scraped from the error body; "Not Applicable" when absent. */
  readonly requestId: string;

  constructor(kind: ApiErrorKind, status: number | undefined, requestId: string) {
    super(`API ${kind} error${status === undefined ? '' : ` (${status})`}`);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.requestId = requestId;
  }
}

/**
 * Async because resolving the token may first REFRESH it — the old app ran
 * `validateAccessToken` at the top of every call (utils.js:50-58). See
 * services/tokenProvider.ts for why the resolver is injected rather than imported.
 */
async function authHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  const token = await resolveAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Extracts the request id from an error body, covering both shapes:
 *   - this backend's `{ error: { message, requestId } }`
 *   - the old upstream's `{ message: "... RequestID: abc" }`, which utils.js:124-137
 *     scraped by finding "RequestID" and slicing after the first colon.
 *
 * The second form still matters: the backend forwards a 4xx/5xx of its own making, but a
 * support ticket quoting an id from before the migration must still resolve the same way.
 */
function extractRequestId(body: unknown): string {
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;

    const errorObj = record.error as Record<string, unknown> | undefined;
    if (errorObj && typeof errorObj.requestId === 'string') {
      return errorObj.requestId;
    }

    const message = typeof record.message === 'string' ? record.message : '';
    if (message.indexOf('RequestID') > -1) {
      const colonIndex = message.indexOf(':');
      if (colonIndex > -1) return message.slice(colonIndex + 1);
    }
  }
  return NOT_APPLICABLE;
}

async function responseError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON or empty body — fall through with "Not Applicable".
  }
  return new ApiError('response', response.status, extractRequestId(body));
}

/**
 * `fetch` rejects only on a network-level failure, so anything thrown here is the
 * equivalent of axios's `error.request` branch — except the session-expired sentinel,
 * which services/okta.ts throws before the request is attempted.
 */
function thrownError(error: unknown): ApiError {
  if (error instanceof Error && error.message === TOKEN_NOT_ELIGIBLE_FOR_REFRESH) {
    return new ApiError('token-expired', 0, NOT_APPLICABLE);
  }
  return new ApiError('network', undefined, NOT_APPLICABLE);
}

/** The `X-TimeZone` header the five time-zone-sensitive endpoints send. */
function timeZoneHeader(timeZone?: string): Record<string, string> {
  // The old app passed `selectedDate?.currentDate?.toString()`, which is undefined before
  // a week has been selected, and axios omits an undefined header. Omit it here too — the
  // backend treats an absent header as "do not forward".
  return timeZone === undefined ? {} : { 'X-TimeZone': timeZone };
}

async function apiGet<T>(
  path: string,
  params: Record<string, QueryValue> = {},
  timeZone?: string,
): Promise<T> {
  let response: Response;
  try {
    // Inside the try on purpose: the token refresh is what throws
    // TOKEN_NOT_ELIGIBLE_FOR_REFRESH, and the old app raised that from the same
    // position — before the request — so it surfaces as the session-expired message.
    response = await fetch(`/api/${path}${buildQueryString(params)}`, {
      method: 'GET',
      headers: await authHeaders(timeZoneHeader(timeZone)),
    });
  } catch (error) {
    throw thrownError(error);
  }

  /*
   * A 404 REJECTS, exactly as axios did. It is tempting to treat it as an empty result,
   * but the old app relies on the rejection: getErrorMessage maps 404 to
   * { errorMessage: "No Data To Display", status: 404 }, and the catch blocks render
   * that. Returning null here would silently blank a screen the original explained.
   */
  if (!response.ok) throw await responseError(response);
  if (response.status === 204) return undefined as T;

  return (await response.json().catch(() => undefined)) as T;
}

async function apiMutate<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body: unknown,
  params: Record<string, QueryValue> = {},
  timeZone?: string,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api/${path}${buildQueryString(params)}`, {
      method,
      headers: await authHeaders({
        'Content-Type': 'application/json',
        ...timeZoneHeader(timeZone),
      }),
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw thrownError(error);
  }

  if (!response.ok) throw await responseError(response);
  if (response.status === 204) return undefined as T;

  return (await response.json().catch(() => undefined)) as T;
}

/* ------------------------------------------------------------------ */
/* Endpoint helpers — one per backend route, numbered as in the plan   */
/* ------------------------------------------------------------------ */

/** #1 — utils.js:getDelegatedUsers */
export function getDelegatedUsers(userName: string): Promise<DelegatedUsersResponse> {
  return apiGet<DelegatedUsersResponse>('time-entry/users', { userName });
}

/** The query four endpoints share, in the old app's parameter order. */
export interface WeekScope {
  gteEmployeeNo: number;
  year: number;
  weekNo: number;
  loggedUserGteEmployeeNo: number;
}

/** #2 — utils.js:getEmployeeLeaveHolidayData */
export function getEmployeeLeaveHolidayData(scope: WeekScope): Promise<ApiRecord> {
  return apiGet<ApiRecord>('time-entry/leave-holiday', { ...scope });
}

/** #3 — utils.js:getProjectList */
export function getProjectList(scope: WeekScope): Promise<ApiRecord> {
  return apiGet<ApiRecord>('time-entry/projects', { ...scope });
}

/** #4 — utils.js:getTimeSheetList (X-TimeZone) */
export function getTimeSheetList(
  scope: WeekScope,
  gteProjectId: number,
  timeZone?: string,
): Promise<ApiRecord> {
  return apiGet<ApiRecord>('time-entry/time-sheets', { ...scope, gteProjectId }, timeZone);
}

/** #5 — utils.js:getUnassignedTasks. `gteProjectIds` is a comma-joined list, possibly "". */
export function getUnassignedTasks(
  scope: WeekScope,
  gteProjectIds: string,
): Promise<ApiRecord> {
  return apiGet<ApiRecord>('time-entry/tasks', { ...scope, gteProjectIds });
}

/** #6 — utils.js:getDayWiseTotal */
export function getDayWiseTotal(scope: WeekScope): Promise<ApiRecord> {
  return apiGet<ApiRecord>('time-entry/daily-total', { ...scope });
}

/** #7 — utils.js:getAlertsMethod. Note the distinct `globalEmployeeId` parameter name. */
export function getAlertsMethod(
  globalEmployeeId: number,
  loggedUserGteEmployeeNo: number,
): Promise<ApiRecord> {
  return apiGet<ApiRecord>('time-entry/alerts', { globalEmployeeId, loggedUserGteEmployeeNo });
}

/** #8 — utils.js:getPastWeekLeaveMismatchesData (X-TimeZone) */
export function getPastWeekLeaveMismatchesData(
  scope: WeekScope,
  timeZone?: string,
): Promise<ApiRecord> {
  return apiGet<ApiRecord>('time-entry/leave-validation', { ...scope }, timeZone);
}

/** #9 — utils.js:patchMethod (adds a task despite the name) */
export function addTask(body: unknown): Promise<ApiRecord> {
  return apiMutate<ApiRecord>('POST', 'time-entry/task', body);
}

/** #10 — utils.js:deleteSelectedTaskData. A DELETE carrying a body, as before. */
export function deleteSelectedTaskData(body: unknown): Promise<ApiRecord> {
  return apiMutate<ApiRecord>('DELETE', 'time-entry/tasks', body);
}

/** #11 — utils.js:saveOrSubmitPatchMethod. action S = save draft, U = submit. */
export function saveOrSubmit(
  year: number,
  week: number,
  action: 'S' | 'U',
  body: unknown,
  timeZone?: string,
): Promise<ApiRecord> {
  return apiMutate<ApiRecord>('PATCH', `time-entry/${year}/${week}/${action}`, body, {}, timeZone);
}

/** #12 — utils.js:unFreezeMyWeek */
export function unFreezeMyWeek(body: unknown): Promise<ApiRecord> {
  return apiMutate<ApiRecord>('POST', 'time-entry/unfreeze-my-week', body);
}

/** #13 — utils.js:copyWeekMethod (X-TimeZone). Body is empty; the query carries it all. */
export function copyWeek(scope: WeekScope, timeZone?: string): Promise<ApiRecord> {
  return apiMutate<ApiRecord>('POST', 'time-entry/copy-week', {}, { ...scope }, timeZone);
}

/** #14 — utils.js:copyUnAssignTaskMethod (X-TimeZone) */
export function copyUnassignedTask(scope: WeekScope, timeZone?: string): Promise<ApiRecord> {
  return apiMutate<ApiRecord>(
    'POST',
    'time-entry/copy-unassigned-task',
    {},
    { ...scope },
    timeZone,
  );
}

/** #15 — utils.js:getGlobalTasks. Called only ever with "Y". */
export function getGlobalTasks(isGlobal: 'Y' | 'N' = 'Y'): Promise<ApiRecord> {
  return apiGet<ApiRecord>('source/standard-codes', { isGlobal });
}

/** #16 — utils.js:getFreezeDateValue */
export function getFreezeDateValue(): Promise<ApiRecord> {
  return apiGet<ApiRecord>('source/config', { configType: 'freezeDate' });
}

/** #17 — utils.js:getApplicationGuideInforLink */
export function getApplicationGuideInfoLink(): Promise<ApiRecord> {
  return apiGet<ApiRecord>('source/config', { configType: 'INFO_PAGE' });
}

/** #18 — utils.js:taskDonePatchMethod */
export function taskDonePatch(taskAssignmentId: number, body: unknown): Promise<ApiRecord> {
  return apiMutate<ApiRecord>('PATCH', `source/task-assignments/${taskAssignmentId}`, body);
}

/** #19 — utils.js:postFeedback. Uses the feedback subscription key, server-side. */
export function postFeedback(body: FeedbackBody): Promise<ApiRecord> {
  return apiMutate<ApiRecord>('POST', 'feedback', body);
}

/** Runtime config — replaces the old build-time property.json import. */
export async function getAuthConfig(): Promise<AuthConfig> {
  const response = await fetch('/api/auth-config', { headers: { Accept: 'application/json' } });
  if (!response.ok) {
    throw new Error(`Failed to load auth configuration (status ${response.status})`);
  }
  return (await response.json()) as AuthConfig;
}

/** Exposed for tests only. */
export const __internal = { apiGet, apiMutate, extractRequestId };
